import { BroadcastResults } from "./broadcast-result";
import { ProofResult, ProofResults } from "./proof-result";
import { HttpTrackingConfig } from "../../../ShellCommon/logger/http-interceptor";
import { SdServicesSharedEnums } from "../sdServices-shared-enums";
import { LoggingService } from "../loggingSvc";
import { ServiceDeskConfig } from "../../Startup/serviceDeskConfig";
export interface FortificationService {
  /**
   * Populates a header object with the fortification token for a customerId/customerType combination.
   */
  populateHeaderByCustomerId(
    customerId: string,
    customerIdType: any,
    headerObject: ng.IHttpRequestConfigHeaders
  ): ng.IHttpRequestConfigHeaders;
  /**
   * Populates a header object with the fortification token for a tag string.
   */
  populateHeaderByTag(tag: string, headerObject: ng.IHttpRequestConfigHeaders): ng.IHttpRequestConfigHeaders;
  /**
   * Returns true if the local cache contains a valid fortification token.
   */
  isFortified(customerId: string, customerIdType: any): boolean;
  /**
   * Returns true if the local cache contains a valid fortification token.
   */
  isFortifiedByTag(tag: string): boolean;
  /**
   * Confirms a verification code provided by a customer to an agent.
   */
  confirmCode(customerId: string, customerIdType: any, code: string, tag?: string): ng.IPromise<any>;
  /**
   * Confirms a visit is authenticated and stores the fortification token.
   */
  confirmAuthenticatedVisit(visitId: string, tag?: string): ng.IPromise<boolean>;
  /**
   * Broadcasts verification code to specific proofs if supplied, or to all proofs otherwise.
   */
  broadcastCodes(customerId: string, customerIdType: any, proof?: ProofResult): ng.IPromise<BroadcastResults>;
  /**
   * Gets all proofs for a given customerId / customerIdType combination.
   */
  getProofs(customerId: string, customerIdType: any): ng.IPromise<ProofResults>;
  /**
   * Tests the validity of the fortification token for a given customer and customerId.
   */
  testToken(customerId: string, customerIdType: any): ng.IPromise<string>;
  /**
   * Test the validity of the fortification token for a given tag.
   */
  testTagToken(tag: string, customerId: string): ng.IPromise<string>;
}

type FortificationStatusStorage = {
  [customerIdType: string]: { [customerId: string]: RedemptionResult };
};

type TagFortificationStatusStorage = {
  [tag: string]: RedemptionResult;
};

export class RedemptionResult {
  wasRedemptionSuccessful: boolean;
  fortificationToken: string;
  expiryTime: string;
}

export class CodeRedemptionResult extends RedemptionResult {
  didCodeExist: boolean;
}

export class VisitRedemptionResult extends RedemptionResult {
  customerId: string;
}

export class FortificationServiceImplementation implements FortificationService {
  public modifyHeaders: (headerObject: ng.IHttpRequestConfigHeaders, customerIdType?: any) => void;
  public readonly fortificationUrls = {
    codes: "SDController/api/Fortification/codes/",
    visits: "SDController/api/Fortification/visits/",
    proofs: "SDController/api/Fortification/proofs/",
    testToken: "SDController/api/fortification/sample/"
  };
  private fortificationStorage: FortificationStatusStorage = {};
  private tagStorage: { [tag: string]: RedemptionResult } = {};
  private localStorageKey = "FortificationServiceLocalStorageKey";
  private tagLocalStorageKey = "FortificationServiceTagLocalStorageKey";
  private fortificationTokenHeaderName = "ms-fortification";
  // @ngInject
  constructor(
    private $http: ng.IHttpService,
    private $q: ng.IQService,
    private sdServicesSharedEnums: SdServicesSharedEnums,
    private loggingService: LoggingService,
    private sdConfig: ServiceDeskConfig
  ) {
    this.fortificationStorage = this.loadFortificationStorage();
    this.tagStorage = this.loadTagStorage();
  }

  private getServiceDeskUrl(): string {
    return this.sdConfig.sdBaseUrl;
  }

  private logEvent(errorType: any, errorDetail: string, data?: any): void {
    this.loggingService.logError(errorDetail, errorType);
  }

  private beforeFortificationRequest(): ng.IPromise<void> {
    return this.$q.resolve();
  }

  private getStringForCustomerIdType(customerIdType: any): string {
    switch (customerIdType) {
      case this.sdServicesSharedEnums.customerIdType.Puid:
        return "Puid";
      case this.sdServicesSharedEnums.customerIdType.Hardware:
        return "Hardware";
      case this.sdServicesSharedEnums.customerIdType.Email:
        return "Email";
      case this.sdServicesSharedEnums.customerIdType.ServiceRequest:
        return "ServiceRequest";
      case this.sdServicesSharedEnums.customerIdType.MetroId:
        return "MetroId";
      case this.sdServicesSharedEnums.customerIdType.SkypeId:
        return "SkypeId";
      case this.sdServicesSharedEnums.customerIdType.CpmEmail:
        return "CpmEmail";
      default:
        return "Unknown";
    }
  }

  public populateHeaderByCustomerId(
    customerId: string,
    customerIdType: any,
    headerObject: ng.IHttpRequestConfigHeaders
  ): ng.IHttpRequestConfigHeaders {
    if (!headerObject) {
      return;
    }

    let statusForType = this.fortificationStorage[this.getStringForCustomerIdType(customerIdType)];
    if (statusForType) {
      let resultForCustomer = statusForType[customerId];
      if (resultForCustomer && !this.isExpired(resultForCustomer)) {
        headerObject[this.fortificationTokenHeaderName] = resultForCustomer.fortificationToken;
      } else {
        headerObject[this.fortificationTokenHeaderName] = "invalid";
      }
    }

    return headerObject;
  }

  public populateHeaderByTag(tag: string, headerObject: ng.IHttpRequestConfigHeaders): ng.IHttpRequestConfigHeaders {
    if (!headerObject) {
      return;
    }

    let resultForCustomer = this.tagStorage[tag];
    if (resultForCustomer && !this.isExpired(resultForCustomer)) {
      headerObject[this.fortificationTokenHeaderName] = resultForCustomer.fortificationToken;
    } else {
      headerObject[this.fortificationTokenHeaderName] = "invalid";
    }

    return headerObject;
  }

  public isFortified(customerId: string, customerIdType: any): boolean {
    let statusForType = this.fortificationStorage[this.getStringForCustomerIdType(customerIdType)];
    if (statusForType) {
      let resultForCustomer = statusForType[customerId];
      if (resultForCustomer && !this.isExpired(resultForCustomer)) {
        return true;
      }
    }

    return false;
  }

  public isFortifiedByTag(tag: string): boolean {
    let resultForCustomer = this.tagStorage[tag];
    return resultForCustomer && !this.isExpired(resultForCustomer);
  }

  public confirmCode(customerId: string, customerIdType: any, code: string, tag?: string): ng.IPromise<any> {
    let postData = {
      customerIdType: this.getStringForCustomerIdType(customerIdType),
      customerId: customerId
    };

    return this.beforeFortificationRequest().then(() => {
      let headers = {};
      if (this.modifyHeaders && typeof this.modifyHeaders === "function") {
        this.modifyHeaders(headers, customerIdType);
      }
      let config: HttpTrackingConfig = {
        headers: headers,
        dependencyOperationName: "RedeemFortification"
      };
      return this.$http.post(this.getServiceDeskUrl() + this.fortificationUrls.codes + code, postData, config).then(
        (result: ng.IHttpPromiseCallbackArg<CodeRedemptionResult>) => {
          let response = result.data;
          if (!response) {
            this.logEvent(
              this.sdServicesSharedEnums.fortificationEventType.UnexpectedServiceResponse,
              "There was an unexpected service response when submitted the fortification code."
            );
            return <ng.IPromise<any>>this.$q.reject("Invalid response received from fortification code submission.");
          }
          if (!response.wasRedemptionSuccessful) {
            this.logEvent(
              this.sdServicesSharedEnums.fortificationEventType.InvalidCodeSubmitted,
              "An invalid fortification code was submitted."
            );
            return response.didCodeExist
              ? this.sdServicesSharedEnums.codeRedemptionStatus.InvalidCodeSubmitted
              : this.sdServicesSharedEnums.codeRedemptionStatus.ExpiredCode;
          }
          this.updateStorage(customerId, postData.customerIdType, response);
          if (tag) {
            this.updateTagStorage(tag, response);
          }
          return this.sdServicesSharedEnums.codeRedemptionStatus.SuccessfulRedemption;
        },
        (error: ng.IHttpPromiseCallbackArg<{}>) => {
          this.logEvent(
            this.sdServicesSharedEnums.fortificationEventType.ServiceRequestFailed,
            "The service request to the fortification service failed.",
            error.data
          );
          return this.$q.reject("An error occurred during the service request.");
        }
      );
    });
  }

  public confirmAuthenticatedVisit(visitId: string, tag?: string): ng.IPromise<boolean> {
    return this.beforeFortificationRequest().then(() => {
      let headers = {};
      if (this.modifyHeaders && typeof this.modifyHeaders === "function") {
        this.modifyHeaders(headers);
      }
      let config: HttpTrackingConfig = { headers: headers, dependencyOperationName: "RedeemAuthenticatedVisit" };
      return this.$http.post(this.getServiceDeskUrl() + this.fortificationUrls.visits + visitId, {}, config).then(
        (result: ng.IHttpPromiseCallbackArg<VisitRedemptionResult>) => {
          let response = result.data;
          if (!response) {
            this.logEvent(
              this.sdServicesSharedEnums.fortificationEventType.UnexpectedServiceResponse,
              "There was an unexpected service response when verifying the authenticated visit."
            );
            return <ng.IPromise<boolean>>(
              this.$q.reject("Invalid response received from authenticated visit redemption.")
            );
          }
          if (!response.wasRedemptionSuccessful) {
            this.logEvent(
              this.sdServicesSharedEnums.fortificationEventType.InvalidAuthenticatedVisit,
              "The visit attempted for redemption was invalid."
            );
            return false;
          }
          this.updateStorage(
            response.customerId,
            this.getStringForCustomerIdType(this.sdServicesSharedEnums.customerIdType.Puid),
            response
          );
          if (tag) {
            this.updateTagStorage(tag, response);
          }
          return true;
        },
        (error: ng.IHttpPromiseCallbackArg<{}>) => {
          this.logEvent(
            this.sdServicesSharedEnums.fortificationEventType.ServiceRequestFailed,
            "The service request to the fortification service failed.",
            error.data
          );
          return false;
        }
      );
    });
  }

  public testToken(customerId: string, customerIdType: any): ng.IPromise<string> {
    return this.beforeFortificationRequest().then(() => {
      let headers = {};
      if (this.modifyHeaders && typeof this.modifyHeaders === "function") {
        this.modifyHeaders(headers);
      }
      let config: HttpTrackingConfig = {
        headers: this.populateHeaderByCustomerId(customerId, customerIdType, headers),
        dependencyOperationName: "SampleGetProtectedData"
      };
      return this.$http.get(this.getServiceDeskUrl() + this.fortificationUrls.testToken + customerId, config).then(
        (result: ng.IHttpPromiseCallbackArg<boolean>) => {
          return result.data
            ? "The request was successfully fortified"
            : "The request had an invalid fortification token";
        },
        (error) => {
          return JSON.stringify(error);
        }
      );
    });
  }

  public testTagToken(tag: string, customerId: string): ng.IPromise<string> {
    return this.beforeFortificationRequest().then(() => {
      let headers = {};
      if (this.modifyHeaders && typeof this.modifyHeaders === "function") {
        this.modifyHeaders(headers);
      }
      let config: HttpTrackingConfig = {
        headers: this.populateHeaderByTag(tag, headers),
        dependencyOperationName: "SampleGetProtectedData"
      };
      return this.$http.get(this.getServiceDeskUrl() + this.fortificationUrls.testToken + customerId, config).then(
        (result: ng.IHttpPromiseCallbackArg<boolean>) => {
          return result.data
            ? "The request was successfully fortified"
            : "The request had an invalid fortification token";
        },
        (error) => {
          return JSON.stringify(error);
        }
      );
    });
  }

  public broadcastCodes(customerId: string, customerIdType: any, proof?: ProofResult): ng.IPromise<BroadcastResults> {
    let postData = {
      customerIdType: this.getStringForCustomerIdType(customerIdType),
      customerId: customerId,
      specificProofs: proof ? [proof] : undefined
    };

    return this.beforeFortificationRequest().then(() => {
      let headers = {};
      if (this.modifyHeaders && typeof this.modifyHeaders === "function") {
        this.modifyHeaders(headers, customerIdType);
      }
      let config: HttpTrackingConfig = {
        headers: headers,
        dependencyOperationName: "SendFortificationCode"
      };
      return this.$http.post(this.getServiceDeskUrl() + this.fortificationUrls.codes, postData, config).then(
        (result: ng.IHttpPromiseCallbackArg<BroadcastResults>) => {
          let results = result.data;
          if (results && results.proofResults) {
            return results;
          }

          this.logEvent(
            this.sdServicesSharedEnums.fortificationEventType.UnexpectedServiceResponse,
            "Unexpected proof results were found when broadcasting fortification codes."
          );
          return <ng.IPromise<BroadcastResults>>this.$q.reject("Unexpected proof results were found.");
        },
        (error) => {
          this.logEvent(
            this.sdServicesSharedEnums.fortificationEventType.ServiceRequestFailed,
            "Service call failed when broadcasting proofs.",
            error
          );
          return this.$q.reject(error);
        }
      );
    });
  }

  public getProofs(customerId: string, customerIdType: any): ng.IPromise<ProofResults> {
    let customerIdTypeString = this.getStringForCustomerIdType(customerIdType);
    return this.beforeFortificationRequest().then(() => {
      let headers = {};
      if (this.modifyHeaders && typeof this.modifyHeaders === "function") {
        this.modifyHeaders(headers, customerIdType);
      }
      let config: HttpTrackingConfig = {
        headers: headers,
        dependencyOperationName: "GetCustomerProofs"
      };
      return this.$http
        .get(this.getServiceDeskUrl() + this.fortificationUrls.proofs + customerIdTypeString + "/" + customerId, config)
        .then(
          (result: ng.IHttpPromiseCallbackArg<ProofResults>) => {
            return result.data;
          },
          (error) => {
            this.logEvent(
              this.sdServicesSharedEnums.fortificationEventType.ServiceRequestFailed,
              "Service request failed when getting proofs for customer.",
              error
            );
            return this.$q.reject(error);
          }
        );
    });
  }

  private isExpired(fortificationResult: RedemptionResult): boolean {
    let fortifiedDate = new Date(fortificationResult.expiryTime);
    return fortifiedDate.getTime() < Date.now();
  }

  // Updates our localStorage cache of fortification tokens by customerid and idtype.
  //  We "read back" the existing codes from local storage before writing to make sure that we don't lose any changes.
  private updateStorage(customerId: string, customerIdType: string, fortificationResult: RedemptionResult) {
    let storageToUpdate = this.loadFortificationStorage();
    let existingTypeStorage = storageToUpdate[customerIdType];
    if (!existingTypeStorage) {
      existingTypeStorage = {};
      storageToUpdate[customerIdType] = existingTypeStorage;
    }

    existingTypeStorage[customerId] = fortificationResult;
    // after we load from storage, we make sure to set our new dictionary back in to remove expired entries.
    localStorage.setItem(this.localStorageKey, JSON.stringify(storageToUpdate));
    this.fortificationStorage = storageToUpdate;
  }

  // Updates our localStorage cache of fortification tokens by tag.
  //  We "read back" the existing codes from local storage before writing to make sure that we don't lose any changes.
  private updateTagStorage(tag: string, fortificationResult: RedemptionResult) {
    let storageToUpdate = this.loadTagStorage();
    storageToUpdate[tag] = fortificationResult;
    localStorage.setItem(this.tagLocalStorageKey, JSON.stringify(storageToUpdate));
    this.tagStorage = storageToUpdate;
  }

  private loadTagStorage(): TagFortificationStatusStorage {
    let tagFortificationStorage: TagFortificationStatusStorage = {};
    let storedValue: string = localStorage.getItem(this.tagLocalStorageKey);
    if (!storedValue) {
      return tagFortificationStorage;
    }

    let oldStorage: TagFortificationStatusStorage = JSON.parse(storedValue);
    Object.keys(oldStorage).forEach((tag) => {
      let redemptionResult = oldStorage[tag];
      if (redemptionResult && !this.isExpired(redemptionResult)) {
        tagFortificationStorage[tag] = redemptionResult;
      }
    });

    return tagFortificationStorage;
  }

  private loadFortificationStorage(): FortificationStatusStorage {
    let fortificationStorage: FortificationStatusStorage = {};
    let storedValue: string = localStorage.getItem(this.localStorageKey);
    if (!storedValue) {
      return fortificationStorage;
    }

    let oldStorage: FortificationStatusStorage = JSON.parse(storedValue);
    if (!oldStorage) {
      return fortificationStorage;
    }

    Object.keys(oldStorage).forEach((customerIdType) => {
      let resultsByCustomerId = oldStorage[customerIdType];
      Object.keys(resultsByCustomerId).forEach((customerId) => {
        let redemptionResult = resultsByCustomerId[customerId];
        if (redemptionResult && !this.isExpired(redemptionResult)) {
          let existingTypeStorage = fortificationStorage[customerIdType];
          if (!existingTypeStorage) {
            existingTypeStorage = {};
            fortificationStorage[customerIdType] = existingTypeStorage;
          }

          existingTypeStorage[customerId] = redemptionResult;
        }
      });
    });

    return fortificationStorage;
  }
}
